import { useEffect, useState, useCallback } from "react";
import { MessageSquare, Send, Trash2, Check, RotateCcw, X, Reply } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * DocumentComments — right-rail thread panel for a document.
 *
 * Iter34 user request: "Comments feature for documents — full thread +
 * resolve workflow." Backend endpoints already exist
 * (`/api/documents/{id}/comments`); this component just wires the UI:
 *   - List top-level comments + nested replies (1 level deep).
 *   - Add a new top-level comment.
 *   - Reply to a comment.
 *   - Resolve / unresolve (any user with doc access).
 *   - Edit / delete (only the original author).
 *   - Filter resolved out by default; toggle to show.
 */
export default function DocumentComments({ docId, onClose }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null); // comment_id
  const [replyDraft, setReplyDraft] = useState("");
  const [editing, setEditing] = useState(null); // comment_id
  const [editDraft, setEditDraft] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [posting, setPosting] = useState(false);

  const reload = useCallback(async () => {
    if (!docId) return;
    try {
      const { data } = await api.get(`/documents/${docId}/comments`);
      setComments(Array.isArray(data) ? data : []);
    } catch {
      setComments([]);
    }
  }, [docId]);

  useEffect(() => { reload(); }, [reload]);

  // Reset draft state when doc changes.
  useEffect(() => { setDraft(""); setReplyTo(null); setReplyDraft(""); setEditing(null); }, [docId]);

  // Iter35: when user picks "Add Comment" from the editor right-click,
  // pre-fill the draft with the selected snippet wrapped in a quote so
  // the comment author has context.
  useEffect(() => {
    const onAdd = (e) => {
      if (!e.detail || e.detail.docId !== docId) return;
      const snippet = (e.detail.snippet || "").trim();
      if (!snippet) return;
      setDraft((cur) => cur ? cur : `> ${snippet}\n\n`);
    };
    window.addEventListener("crescendo:doc-add-comment", onAdd);
    return () => window.removeEventListener("crescendo:doc-add-comment", onAdd);
  }, [docId]);

  const post = async () => {
    if (!draft.trim() || posting) return;
    setPosting(true);
    try {
      await api.post(`/documents/${docId}/comments`, { body: draft.trim() });
      setDraft("");
      await reload();
    } catch {}
    setPosting(false);
  };

  const postReply = async (parentId) => {
    if (!replyDraft.trim()) return;
    try {
      await api.post(`/documents/${docId}/comments`, { body: replyDraft.trim(), parent_id: parentId });
      setReplyDraft("");
      setReplyTo(null);
      await reload();
    } catch {}
  };

  const saveEdit = async (cmt) => {
    if (!editDraft.trim()) return;
    try {
      await api.put(`/documents/${docId}/comments/${cmt.comment_id}`, { body: editDraft.trim() });
      setEditing(null);
      setEditDraft("");
      await reload();
    } catch {}
  };

  const remove = async (cmt) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/documents/${docId}/comments/${cmt.comment_id}`);
      await reload();
    } catch {}
  };

  const toggleResolve = async (cmt) => {
    try {
      await api.put(`/documents/${docId}/comments/${cmt.comment_id}/resolve`, { resolved: !cmt.resolved });
      await reload();
    } catch {}
  };

  const tops = comments.filter((c) => !c.parent_id);
  const repliesOf = (id) => comments.filter((c) => c.parent_id === id);
  const visibleTops = showResolved ? tops : tops.filter((c) => !c.resolved);
  const resolvedCount = tops.filter((c) => c.resolved).length;

  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      const diff = Date.now() - d.getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return d.toLocaleDateString();
    } catch { return ""; }
  };

  const renderAvatar = (c) => (
    <div className="w-6 h-6 rounded-full bg-[#0A0A0A] border border-[#2A2A2A] overflow-hidden flex items-center justify-center flex-shrink-0">
      {c.user_picture ? <img src={c.user_picture} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] font-medium">{(c.user_name || "?")[0]?.toUpperCase()}</span>}
    </div>
  );

  return (
    <aside
      className="absolute right-0 top-0 bottom-0 z-[60] w-80 flex-shrink-0 border-l border-[#2A2A2A] bg-[#0F0F0F] flex flex-col shadow-2xl"
      data-testid="doc-comments-panel"
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-[#2A2A2A] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-[#A1A1AA]" />
          <span className="text-xs font-semibold text-[#EDEDED]">Comments</span>
          {tops.length > 0 && (
            <span className="text-[10px] text-[#A1A1AA]">{visibleTops.length}{showResolved ? "" : ` of ${tops.length}`}</span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]"
          data-testid="doc-comments-close"
          title="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Resolved toggle */}
      {resolvedCount > 0 && (
        <div className="px-3 py-1.5 border-b border-[#2A2A2A] flex items-center justify-between">
          <span className="text-[10px] text-[#A1A1AA]">{resolvedCount} resolved</span>
          <button
            onClick={() => setShowResolved((v) => !v)}
            className="text-[10px] text-[#5A90F5] hover:underline"
            data-testid="doc-comments-toggle-resolved"
          >
            {showResolved ? "Hide" : "Show"}
          </button>
        </div>
      )}

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="doc-comments-list">
        {visibleTops.length === 0 && (
          <div className="text-[11px] text-[#A1A1AA] text-center py-8">
            No comments yet. Add one below.
          </div>
        )}
        {visibleTops.map((c) => {
          const replies = repliesOf(c.comment_id);
          const isAuthor = user?.user_id === c.user_id;
          const isEditing = editing === c.comment_id;
          return (
            <div
              key={c.comment_id}
              className={`rounded-md border p-2.5 ${c.resolved ? "border-[#2A2A2A]/50 bg-[#0A0A0A] opacity-60" : "border-[#2A2A2A] bg-[#141414]"}`}
              data-testid={`doc-comment-${c.comment_id}`}
            >
              <div className="flex items-start gap-2">
                {renderAvatar(c)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-semibold text-[#EDEDED] truncate">{c.user_name || "User"}</span>
                    <span className="text-[9px] text-[#52525B]">{fmtTime(c.created_at)}</span>
                    {c.resolved && <span className="text-[9px] px-1 rounded bg-[#22C55E]/15 text-[#22C55E] uppercase tracking-wider">Resolved</span>}
                  </div>
                  {isEditing ? (
                    <div className="mt-1">
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={2}
                        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5] resize-none"
                        data-testid={`doc-comment-edit-${c.comment_id}`}
                      />
                      <div className="flex items-center gap-1 mt-1">
                        <button
                          onClick={() => saveEdit(c)}
                          className="text-[10px] px-2 py-0.5 rounded bg-[#5A90F5] text-white hover:bg-[#4a80e5]"
                          data-testid={`doc-comment-edit-save-${c.comment_id}`}
                        >Save</button>
                        <button
                          onClick={() => { setEditing(null); setEditDraft(""); }}
                          className="text-[10px] px-2 py-0.5 rounded text-[#A1A1AA] hover:bg-[#1a1a1a]"
                        >Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-0.5 text-[11px] text-[#D4D4D8] whitespace-pre-wrap break-words">{c.body}</div>
                  )}
                </div>
              </div>

              {/* Action row */}
              {!isEditing && (
                <div className="flex items-center gap-2 mt-2 pl-8 text-[10px] text-[#A1A1AA]">
                  <button
                    onClick={() => { setReplyTo(c.comment_id); setReplyDraft(""); }}
                    className="hover:text-[#EDEDED] inline-flex items-center gap-1"
                    data-testid={`doc-comment-reply-btn-${c.comment_id}`}
                  >
                    <Reply className="w-3 h-3" /> Reply
                  </button>
                  <button
                    onClick={() => toggleResolve(c)}
                    className="hover:text-[#EDEDED] inline-flex items-center gap-1"
                    data-testid={`doc-comment-resolve-${c.comment_id}`}
                  >
                    {c.resolved ? <><RotateCcw className="w-3 h-3" /> Reopen</> : <><Check className="w-3 h-3" /> Resolve</>}
                  </button>
                  {isAuthor && (
                    <>
                      <button
                        onClick={() => { setEditing(c.comment_id); setEditDraft(c.body); }}
                        className="hover:text-[#EDEDED]"
                        data-testid={`doc-comment-edit-btn-${c.comment_id}`}
                      >Edit</button>
                      <button
                        onClick={() => remove(c)}
                        className="hover:text-[#FF3B30] inline-flex items-center gap-1"
                        data-testid={`doc-comment-delete-${c.comment_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Replies */}
              {replies.length > 0 && (
                <div className="mt-2 pl-8 space-y-2 border-l border-[#2A2A2A] ml-2">
                  {replies.map((r) => {
                    const isReplyAuthor = user?.user_id === r.user_id;
                    const isEditingReply = editing === r.comment_id;
                    return (
                      <div key={r.comment_id} className="flex items-start gap-2 pl-2" data-testid={`doc-comment-reply-${r.comment_id}`}>
                        {renderAvatar(r)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-semibold text-[#EDEDED]">{r.user_name || "User"}</span>
                            <span className="text-[9px] text-[#52525B]">{fmtTime(r.created_at)}</span>
                          </div>
                          {isEditingReply ? (
                            <div className="mt-1">
                              <textarea
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                rows={2}
                                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5] resize-none"
                              />
                              <div className="flex items-center gap-1 mt-1">
                                <button onClick={() => saveEdit(r)} className="text-[10px] px-2 py-0.5 rounded bg-[#5A90F5] text-white hover:bg-[#4a80e5]">Save</button>
                                <button onClick={() => { setEditing(null); setEditDraft(""); }} className="text-[10px] px-2 py-0.5 rounded text-[#A1A1AA] hover:bg-[#1a1a1a]">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-[11px] text-[#D4D4D8] whitespace-pre-wrap break-words">{r.body}</div>
                          )}
                          {isReplyAuthor && !isEditingReply && (
                            <div className="flex items-center gap-2 mt-1 text-[9px] text-[#A1A1AA]">
                              <button onClick={() => { setEditing(r.comment_id); setEditDraft(r.body); }} className="hover:text-[#EDEDED]">Edit</button>
                              <button onClick={() => remove(r)} className="hover:text-[#FF3B30]">Delete</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Reply input */}
              {replyTo === c.comment_id && (
                <div className="mt-2 pl-8 flex items-start gap-1.5">
                  <textarea
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        postReply(c.comment_id);
                      }
                    }}
                    placeholder="Write a reply… (⌘+Enter to send)"
                    rows={2}
                    className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5] resize-none"
                    data-testid={`doc-comment-reply-input-${c.comment_id}`}
                    autoFocus
                  />
                  <button
                    onClick={() => postReply(c.comment_id)}
                    disabled={!replyDraft.trim()}
                    className="p-1.5 rounded bg-[#5A90F5] text-white hover:bg-[#4a80e5] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`doc-comment-reply-send-${c.comment_id}`}
                  >
                    <Send className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New top-level comment */}
      <div className="border-t border-[#2A2A2A] p-3 pb-14">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              post();
            }
          }}
          placeholder="Add a comment… (⌘+Enter to send)"
          rows={2}
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5] resize-none"
          data-testid="doc-comment-new-input"
        />
        <div className="flex items-center justify-end mt-1.5">
          <button
            onClick={post}
            disabled={!draft.trim() || posting}
            className="text-[10px] px-2.5 py-1 rounded bg-[#5A90F5] text-white hover:bg-[#4a80e5] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            data-testid="doc-comment-new-send"
          >
            <Send className="w-3 h-3" /> Comment
          </button>
        </div>
      </div>
    </aside>
  );
}
